/**
 * A central de notificações (0151) do lado da tela: o aviso como chega do banco e o
 * texto que a pessoa lê.
 *
 * O banco guarda só referências; os nomes chegam junto, lidos na hora com a permissão
 * de quem lê (`minhas_notificacoes`). Sem o nome — quem lê não alcança aquele contato
 * —, o texto diz o que houve sem dizer com quem. Sem React, para testar a regra sem
 * montar tela.
 */

import { dataSimples, moeda } from '@/comuns/formato';

export const TIPOS_DE_NOTIFICACAO = [
    'agendamento.novo', 'agendamento.cancelado', 'agendamento.remarcado', 'agendamento.falta',
    'whatsapp.sem_resposta', 'canal.desconectado', 'lead.novo', 'tarefa.atribuida', 'tarefa.vence_hoje',
    'fatura.vencida', 'fatura.paga', 'teste.acabando', 'convite.aceito',
    'estoque.minimo', 'compra.atrasada', 'automacao.falhou', 'ia.perto_do_teto',
    'agenda_google.desconectada', 'webhook.falhando',
] as const;

export type TipoDeNotificacao = typeof TIPOS_DE_NOTIFICACAO[number];

export interface NomesDaNotificacao {
    contato?: string;
    profissional?: string;
    produto?: string;
    tarefa?: string;
    canal?: string;
    membro?: string;
}

export interface Notificacao {
    id: string;
    tipo: TipoDeNotificacao;
    /** ISO 8601. */
    criadaEm: string;
    dados: Record<string, unknown>;
    nomes: NomesDaNotificacao;
}

export type GrupoDeNotificacao = 'Agenda' | 'Atendimento e vendas' | 'Financeiro e conta' | 'Operação';

export interface TextoDaNotificacao {
    titulo: string;
    texto: string;
    /** Para onde o clique leva. */
    destino: string;
    /** Nome do ícone Lucide. */
    icone: string;
    grupo: GrupoDeNotificacao;
}

const TIPOS = new Set<string>(TIPOS_DE_NOTIFICACAO);
const FUSO = 'America/Sao_Paulo';

function texto(v: unknown): string | undefined {
    return typeof v === 'string' && v.trim() ? v.trim() : undefined;
}

/** A linha de `minhas_notificacoes`, ou nulo quando o tipo não é um que a tela conhece. */
export function paraNotificacao(linha: Record<string, unknown>): Notificacao | null {
    const tipo = String(linha.tipo ?? '');
    const id = texto(linha.id);
    const criadaEm = texto(linha.criada_em);
    if (!TIPOS.has(tipo) || !id || !criadaEm) return null;
    const nomes: NomesDaNotificacao = {};
    for (const chave of ['contato', 'profissional', 'produto', 'tarefa', 'canal', 'membro'] as const) {
        const nome = texto(linha[chave]);
        if (nome) nomes[chave] = nome;
    }
    const dados = linha.dados && typeof linha.dados === 'object' && !Array.isArray(linha.dados)
        ? linha.dados as Record<string, unknown>
        : {};
    return { id, tipo: tipo as TipoDeNotificacao, criadaEm, dados, nomes };
}

/** `15/09 às 14:00`, no fuso da clínica. */
export function diaEHora(iso: unknown): string {
    const d = new Date(String(iso ?? ''));
    if (Number.isNaN(d.getTime())) return '';
    const dia = d.toLocaleDateString('pt-BR', { timeZone: FUSO, day: '2-digit', month: '2-digit' });
    const hora = d.toLocaleTimeString('pt-BR', { timeZone: FUSO, hour: '2-digit', minute: '2-digit' });
    return `${dia} às ${hora}`;
}

function hora(iso: unknown): string {
    const d = new Date(String(iso ?? ''));
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('pt-BR', { timeZone: FUSO, hour: '2-digit', minute: '2-digit' });
}

function valor(v: unknown): string {
    const n = Number(v);
    return Number.isFinite(n) ? moeda(n) : '';
}

export function textoDaNotificacao(n: Notificacao): TextoDaNotificacao {
    const { dados: d, nomes } = n;
    const quem = nomes.contato ?? 'Paciente';
    const com = nomes.profissional ? ` com ${nomes.profissional}` : '';

    switch (n.tipo) {
        case 'agendamento.novo':
            return { grupo: 'Agenda', icone: 'calendar-plus', destino: '/app/agenda', titulo: 'Novo agendamento', texto: `${quem} · ${diaEHora(d.inicio_em)}${com}` };
        case 'agendamento.cancelado':
            return { grupo: 'Agenda', icone: 'calendar-x', destino: '/app/agenda', titulo: 'Agendamento cancelado', texto: `${quem} · ${diaEHora(d.inicio_em)}${com}` };
        case 'agendamento.remarcado':
            return {
                grupo: 'Agenda', icone: 'calendar-clock', destino: '/app/agenda', titulo: 'Agendamento remarcado',
                texto: `${quem} · de ${diaEHora(d.inicio_anterior)} para ${diaEHora(d.inicio_em)}${com}`,
            };
        case 'agendamento.falta':
            return { grupo: 'Agenda', icone: 'user-x', destino: '/app/agenda', titulo: 'Paciente faltou', texto: `${quem} · ${diaEHora(d.inicio_em)}${com}` };
        case 'whatsapp.sem_resposta':
            return {
                grupo: 'Atendimento e vendas', icone: 'message-circle', destino: '/app/comunicacao', titulo: 'WhatsApp sem resposta',
                texto: `${nomes.contato ?? 'Um contato'} espera resposta desde ${hora(d.desde)}`,
            };
        case 'canal.desconectado':
            return {
                grupo: 'Atendimento e vendas', icone: 'wifi-off', destino: '/app/configuracoes/integracoes', titulo: 'Canal desconectado',
                texto: `${nomes.canal ?? 'O canal de WhatsApp'} caiu. Reconecte em Configurações › Integrações.`,
            };
        case 'lead.novo':
            return { grupo: 'Atendimento e vendas', icone: 'user-plus', destino: '/app/crm', titulo: 'Lead novo', texto: `${nomes.contato ?? 'Um contato novo'} entrou no funil` };
        case 'tarefa.atribuida':
            return {
                grupo: 'Atendimento e vendas', icone: 'list-todo', destino: DESTINO_DAS_TAREFAS, titulo: 'Tarefa para você',
                texto: `${nomes.tarefa ?? 'Uma tarefa'}${texto(d.vence_em) ? ` · vence em ${dataSimples(String(d.vence_em))}` : ''}`,
            };
        case 'tarefa.vence_hoje':
            return { grupo: 'Atendimento e vendas', icone: 'list-todo', destino: DESTINO_DAS_TAREFAS, titulo: 'Tarefa vence hoje', texto: nomes.tarefa ?? 'Uma tarefa vence hoje' };
        case 'fatura.vencida':
            return {
                grupo: 'Financeiro e conta', icone: 'receipt', destino: '/app/configuracoes/assinatura', titulo: 'Fatura vencida',
                texto: `Fatura de ${String(d.competencia ?? '')} (${valor(d.valor_centavos)}) venceu em ${dataSimples(texto(d.vencimento))}`,
            };
        case 'fatura.paga':
            return {
                grupo: 'Financeiro e conta', icone: 'circle-check', destino: '/app/configuracoes/assinatura', titulo: 'Pagamento confirmado',
                texto: `Fatura de ${String(d.competencia ?? '')} (${valor(d.valor_centavos)}) paga`,
            };
        case 'teste.acabando':
            return {
                grupo: 'Financeiro e conta', icone: 'clock', destino: '/app/configuracoes/assinatura', titulo: 'O teste acaba em 3 dias',
                texto: `O teste termina em ${dataSimples(texto(d.teste_ate))}. Nesse dia entra a primeira cobrança.`,
            };
        case 'convite.aceito':
            return { grupo: 'Financeiro e conta', icone: 'user-check', destino: '/app/configuracoes/equipe', titulo: 'Convite aceito', texto: `${nomes.membro ?? 'Uma pessoa'} entrou na equipe` };
        case 'estoque.minimo':
            return {
                grupo: 'Operação', icone: 'package', destino: '/app/estoque', titulo: 'Estoque abaixo do mínimo',
                texto: `${nomes.produto ?? 'Um produto'}: ${String(d.quantidade ?? '')} (mínimo ${String(d.minimo ?? '')})`,
            };
        case 'compra.atrasada':
            return {
                grupo: 'Operação', icone: 'truck', destino: '/app/compras', titulo: 'Compra atrasada',
                texto: `Pedido com entrega prevista para ${dataSimples(texto(d.previsao_entrega))} ainda não chegou`,
            };
        case 'automacao.falhou':
            return {
                grupo: 'Operação', icone: 'triangle-alert', destino: '/app/automacoes', titulo: 'Envio automático falhou',
                texto: nomes.contato ? `A mensagem para ${nomes.contato} não saiu` : 'Uma mensagem automática não saiu',
            };
        case 'ia.perto_do_teto':
            return {
                grupo: 'Operação', icone: 'gauge', destino: '/app/configuracoes', titulo: 'IA perto do teto do mês',
                texto: `${String(d.percentual ?? '')}% dos tokens do mês já foram usados`,
            };
        case 'agenda_google.desconectada':
            return {
                grupo: 'Agenda', icone: 'calendar-off', destino: '/app', titulo: 'Google Agenda desconectada',
                texto: 'O Google tirou o acesso à sua agenda. Reconecte em Minha conta para os atendimentos voltarem a aparecer lá.',
            };
        case 'webhook.falhando':
            return {
                grupo: 'Operação', icone: 'webhook', destino: '/app/api', titulo: 'Webhook falhando',
                texto: `${typeof d.nome === 'string' ? d.nome : 'Um webhook'} falhou 5 vezes seguidas. Veja as entregas em API e integrações.`,
            };
        default:
            return { grupo: 'Operação', icone: 'bell', destino: '/app', titulo: 'Aviso', texto: '' };
    }
}

/** As tarefas aparecem no Início, com as do dia. */
export const DESTINO_DAS_TAREFAS = '/app';
